import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { 
  User, Heart, Pill, Activity, FileText, Upload, Shield,
  ArrowRight, ArrowLeft, Plus, X, Loader2, CheckCircle, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import api, { createFHIRPatient, createFHIRCondition } from "@/lib/api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const ADL_OPTIONS = [
  { value: "independent", label: "Independent" },
  { value: "needs_assistance", label: "Needs Assistance" },
  { value: "dependent", label: "Dependent/Total Care" }
];

const STEPS = [
  { id: 1, title: "Patient Info", icon: User },
  { id: 2, title: "Insurance", icon: Shield },
  { id: 3, title: "Clinical", icon: Heart },
  { id: 4, title: "Functional", icon: Activity },
  { id: 5, title: "Physician", icon: FileText },
  { id: 6, title: "Documents", icon: Upload }
];

export default function PatientReferralForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    // Patient Demographics
    patient_first_name: "",
    patient_last_name: "",
    patient_dob: "",
    patient_gender: "",
    patient_ssn_last4: "",
    patient_address: "",
    patient_city: "",
    patient_state: "",
    patient_zip: "",
    patient_phone: "",
    patient_email: "",
    
    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    
    // Insurance
    primary_insurance: "",
    primary_insurance_id: "",
    secondary_insurance: "",
    secondary_insurance_id: "",
    medicare_number: "",
    medicaid_number: "",
    
    // Clinical
    primary_diagnosis: "",
    primary_diagnosis_icd10: "",
    secondary_diagnoses: [],
    prognosis: "",
    medications: [],
    current_treatments: [],
    
    // ADLs
    adl_bathing: "",
    adl_dressing: "",
    adl_toileting: "",
    adl_transferring: "",
    adl_eating: "",
    adl_continence: "",
    
    // Vitals
    vitals_date: "",
    vitals_bp: "",
    vitals_hr: "",
    vitals_temp: "",
    vitals_resp: "",
    vitals_o2sat: "",
    vitals_weight: "",
    
    // Labs
    labs_date: "",
    labs_data: {},
    
    // Hospitalizations
    recent_hospitalizations: [],
    
    // Physician
    attending_physician_name: "",
    attending_physician_npi: "",
    attending_physician_phone: "",
    attending_physician_fax: "",
    
    // Referral Details
    referral_reason: "",
    urgency: "routine",
    preferred_contact_method: "phone",
    additional_notes: ""
  });

  const [newMedication, setNewMedication] = useState({ name: "", dosage: "", frequency: "" });
  const [newDiagnosis, setNewDiagnosis] = useState("");
  const [newTreatment, setNewTreatment] = useState("");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addMedication = () => {
    if (newMedication.name) {
      setFormData(prev => ({
        ...prev,
        medications: [...prev.medications, { ...newMedication }]
      }));
      setNewMedication({ name: "", dosage: "", frequency: "" });
    }
  };

  const removeMedication = (index) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const addDiagnosis = () => {
    if (newDiagnosis) {
      setFormData(prev => ({
        ...prev,
        secondary_diagnoses: [...prev.secondary_diagnoses, newDiagnosis]
      }));
      setNewDiagnosis("");
    }
  };

  const addTreatment = () => {
    if (newTreatment) {
      setFormData(prev => ({
        ...prev,
        current_treatments: [...prev.current_treatments, newTreatment]
      }));
      setNewTreatment("");
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const submitReferral = async () => {
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem("facility_token");
      if (!token) {
        toast.error("Please log in to submit referrals");
        navigate("/facility");
        return;
      }

      // Step 1: Create patient in FHIR store (PHI data - Google Cloud Healthcare API)
      const fhirPatientData = {
        given_name: formData.patient_first_name,
        family_name: formData.patient_last_name,
        birth_date: formData.patient_dob || "1900-01-01",
        gender: formData.patient_gender || "unknown",
        telephone: formData.patient_phone || null,
        email: formData.patient_email || null,
        address: {
          line: formData.patient_address || "",
          city: formData.patient_city || "",
          state: formData.patient_state || "",
          postal_code: formData.patient_zip || ""
        }
      };
      
      let fhirPatientId = null;
      try {
        const fhirPatient = await createFHIRPatient(fhirPatientData);
        fhirPatientId = fhirPatient.id;
        
        // Create FHIR Condition for primary diagnosis
        if (formData.primary_diagnosis && fhirPatientId) {
          await createFHIRCondition({
            patient_id: fhirPatientId,
            code: formData.primary_icd10 || "Z51.5",
            code_display: formData.primary_diagnosis,
            clinical_status: "active"
          });
        }
      } catch (fhirError) {
        console.error("FHIR creation error:", fhirError);
        // Continue with referral even if FHIR fails
      }

      // Step 2: Create FormData for file upload with FHIR reference
      const submitData = new FormData();
      const referralDataWithFHIR = {
        ...formData,
        fhir_patient_id: fhirPatientId
      };
      submitData.append("referral_data", JSON.stringify(referralDataWithFHIR));
      
      // Add files
      uploadedFiles.forEach(file => {
        submitData.append("files", file);
      });

      const response = await api.post("/facility/referrals", submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.success) {
        const message = fhirPatientId 
          ? "Referral submitted and patient created in FHIR store!" 
          : "Referral submitted successfully!";
        toast.success(message);
        navigate("/facility/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit referral");
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Patient Demographics */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.patient_first_name}
                  onChange={(e) => handleInputChange("patient_first_name", e.target.value)}
                  placeholder="John"
                  required
                  data-testid="patient-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.patient_last_name}
                  onChange={(e) => handleInputChange("patient_last_name", e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={formData.patient_dob}
                  onChange={(e) => handleInputChange("patient_dob", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select value={formData.patient_gender} onValueChange={(v) => handleInputChange("patient_gender", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>SSN (Last 4 digits)</Label>
                <Input
                  value={formData.patient_ssn_last4}
                  onChange={(e) => handleInputChange("patient_ssn_last4", e.target.value)}
                  placeholder="1234"
                  maxLength={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.patient_phone}
                  onChange={(e) => handleInputChange("patient_phone", e.target.value)}
                  placeholder="(703) 555-0100"
                />
              </div>
            </div>

            <Separator />

            {/* Address */}
            <div className="space-y-4">
              <h3 className="font-semibold">Patient Address</h3>
              <div className="space-y-2">
                <Label>Street Address *</Label>
                <Input
                  value={formData.patient_address}
                  onChange={(e) => handleInputChange("patient_address", e.target.value)}
                  placeholder="123 Main St"
                  required
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.patient_city}
                    onChange={(e) => handleInputChange("patient_city", e.target.value)}
                    placeholder="Falls Church"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Select value={formData.patient_state} onValueChange={(v) => handleInputChange("patient_state", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ZIP *</Label>
                  <Input
                    value={formData.patient_zip}
                    onChange={(e) => handleInputChange("patient_zip", e.target.value)}
                    placeholder="22041"
                    required
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Emergency Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold">Emergency Contact</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleInputChange("emergency_contact_name", e.target.value)}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Relationship *</Label>
                  <Input
                    value={formData.emergency_contact_relationship}
                    onChange={(e) => handleInputChange("emergency_contact_relationship", e.target.value)}
                    placeholder="Spouse"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleInputChange("emergency_contact_phone", e.target.value)}
                    placeholder="(703) 555-0101"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Primary Insurance *</Label>
                <Input
                  value={formData.primary_insurance}
                  onChange={(e) => handleInputChange("primary_insurance", e.target.value)}
                  placeholder="Medicare Part A"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Primary Insurance ID *</Label>
                <Input
                  value={formData.primary_insurance_id}
                  onChange={(e) => handleInputChange("primary_insurance_id", e.target.value)}
                  placeholder="1EG4-TE5-MK72"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Insurance</Label>
                <Input
                  value={formData.secondary_insurance}
                  onChange={(e) => handleInputChange("secondary_insurance", e.target.value)}
                  placeholder="Medicaid"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Insurance ID</Label>
                <Input
                  value={formData.secondary_insurance_id}
                  onChange={(e) => handleInputChange("secondary_insurance_id", e.target.value)}
                  placeholder=""
                />
              </div>
              <div className="space-y-2">
                <Label>Medicare Number</Label>
                <Input
                  value={formData.medicare_number}
                  onChange={(e) => handleInputChange("medicare_number", e.target.value)}
                  placeholder="1EG4-TE5-MK72"
                />
              </div>
              <div className="space-y-2">
                <Label>Medicaid Number</Label>
                <Input
                  value={formData.medicaid_number}
                  onChange={(e) => handleInputChange("medicaid_number", e.target.value)}
                  placeholder=""
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Diagnoses */}
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Primary Diagnosis *</Label>
                  <Input
                    value={formData.primary_diagnosis}
                    onChange={(e) => handleInputChange("primary_diagnosis", e.target.value)}
                    placeholder="Lung Cancer, Stage IV"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>ICD-10 Code</Label>
                  <Input
                    value={formData.primary_diagnosis_icd10}
                    onChange={(e) => handleInputChange("primary_diagnosis_icd10", e.target.value)}
                    placeholder="C34.90"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Secondary Diagnoses</Label>
                <div className="flex gap-2">
                  <Input
                    value={newDiagnosis}
                    onChange={(e) => setNewDiagnosis(e.target.value)}
                    placeholder="Add diagnosis"
                  />
                  <Button type="button" onClick={addDiagnosis}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.secondary_diagnoses.map((dx, idx) => (
                    <Badge key={idx} variant="secondary" className="px-3 py-1">
                      {dx}
                      <button onClick={() => setFormData(prev => ({
                        ...prev,
                        secondary_diagnoses: prev.secondary_diagnoses.filter((_, i) => i !== idx)
                      }))} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Prognosis *</Label>
                <Select value={formData.prognosis} onValueChange={(v) => handleInputChange("prognosis", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select prognosis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="less_than_6_months">Less than 6 months</SelectItem>
                    <SelectItem value="6_to_12_months">6-12 months</SelectItem>
                    <SelectItem value="uncertain">Uncertain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Medications */}
            <div className="space-y-4">
              <h3 className="font-semibold">Current Medications</h3>
              <div className="grid gap-2 md:grid-cols-4">
                <Input
                  value={newMedication.name}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Medication name"
                />
                <Input
                  value={newMedication.dosage}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, dosage: e.target.value }))}
                  placeholder="Dosage"
                />
                <Input
                  value={newMedication.frequency}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, frequency: e.target.value }))}
                  placeholder="Frequency"
                />
                <Button type="button" onClick={addMedication}>
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
              
              {formData.medications.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2">Medication</th>
                        <th className="text-left p-2">Dosage</th>
                        <th className="text-left p-2">Frequency</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.medications.map((med, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{med.name}</td>
                          <td className="p-2">{med.dosage}</td>
                          <td className="p-2">{med.frequency}</td>
                          <td className="p-2">
                            <Button variant="ghost" size="sm" onClick={() => removeMedication(idx)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Separator />

            {/* Vitals */}
            <div className="space-y-4">
              <h3 className="font-semibold">Recent Vitals</h3>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.vitals_date}
                    onChange={(e) => handleInputChange("vitals_date", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Blood Pressure</Label>
                  <Input
                    value={formData.vitals_bp}
                    onChange={(e) => handleInputChange("vitals_bp", e.target.value)}
                    placeholder="120/80"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heart Rate</Label>
                  <Input
                    value={formData.vitals_hr}
                    onChange={(e) => handleInputChange("vitals_hr", e.target.value)}
                    placeholder="72"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temperature</Label>
                  <Input
                    value={formData.vitals_temp}
                    onChange={(e) => handleInputChange("vitals_temp", e.target.value)}
                    placeholder="98.6"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Respiratory Rate</Label>
                  <Input
                    value={formData.vitals_resp}
                    onChange={(e) => handleInputChange("vitals_resp", e.target.value)}
                    placeholder="16"
                  />
                </div>
                <div className="space-y-2">
                  <Label>O2 Saturation</Label>
                  <Input
                    value={formData.vitals_o2sat}
                    onChange={(e) => handleInputChange("vitals_o2sat", e.target.value)}
                    placeholder="98%"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (lbs)</Label>
                  <Input
                    value={formData.vitals_weight}
                    onChange={(e) => handleInputChange("vitals_weight", e.target.value)}
                    placeholder="150"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Assess the patient&apos;s ability to perform Activities of Daily Living (ADLs)
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                { key: "adl_bathing", label: "Bathing" },
                { key: "adl_dressing", label: "Dressing" },
                { key: "adl_toileting", label: "Toileting" },
                { key: "adl_transferring", label: "Transferring" },
                { key: "adl_eating", label: "Eating" },
                { key: "adl_continence", label: "Continence" }
              ].map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label>{label} *</Label>
                  <Select value={formData[key]} onValueChange={(v) => handleInputChange(key, v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {ADL_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Attending Physician Name *</Label>
                <Input
                  value={formData.attending_physician_name}
                  onChange={(e) => handleInputChange("attending_physician_name", e.target.value)}
                  placeholder="Dr. Jane Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>NPI Number</Label>
                <Input
                  value={formData.attending_physician_npi}
                  onChange={(e) => handleInputChange("attending_physician_npi", e.target.value)}
                  placeholder="1234567890"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.attending_physician_phone}
                  onChange={(e) => handleInputChange("attending_physician_phone", e.target.value)}
                  placeholder="(703) 555-0100"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Fax</Label>
                <Input
                  value={formData.attending_physician_fax}
                  onChange={(e) => handleInputChange("attending_physician_fax", e.target.value)}
                  placeholder="(703) 555-0101"
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Reason for Referral *</Label>
                <Textarea
                  value={formData.referral_reason}
                  onChange={(e) => handleInputChange("referral_reason", e.target.value)}
                  placeholder="Describe the reason for hospice referral..."
                  rows={4}
                  required
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Urgency *</Label>
                  <Select value={formData.urgency} onValueChange={(v) => handleInputChange("urgency", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select urgency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent (24-48 hours)</SelectItem>
                      <SelectItem value="emergent">Emergent (Same day)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Preferred Contact Method *</Label>
                  <Select value={formData.preferred_contact_method} onValueChange={(v) => handleInputChange("preferred_contact_method", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="fax">Fax</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => handleInputChange("additional_notes", e.target.value)}
                  placeholder="Any additional information..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <Alert>
              <Upload className="h-4 w-4" />
              <AlertDescription>
                Upload relevant clinical documents such as face sheets, recent labs, H&P, imaging reports, etc.
                Supported formats: PDF, DOC, DOCX, JPG, PNG (max 10MB each)
              </AlertDescription>
            </Alert>

            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Upload Documents</p>
              <p className="text-sm text-muted-foreground mb-4">
                Drag and drop files here or click to browse
              </p>
              <Input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="max-w-xs mx-auto"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </div>

            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Uploaded Files ({uploadedFiles.length})</h4>
                {uploadedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeFile(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                All information submitted is encrypted and HIPAA compliant.
              </AlertDescription>
            </Alert>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate("/facility/dashboard")} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold">New Patient Referral</h1>
            <p className="text-muted-foreground">Submit a secure patient referral to Goodwin Hospice</p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <Progress value={progress} className="h-2 mb-4" />
            <div className="flex justify-between">
              {STEPS.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id;
                return (
                  <div 
                    key={step.id}
                    className={`flex flex-col items-center cursor-pointer ${
                      isActive ? "text-primary" : isComplete ? "text-green-600" : "text-muted-foreground"
                    }`}
                    onClick={() => setCurrentStep(step.id)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                      isActive ? "bg-primary text-white" : isComplete ? "bg-green-100" : "bg-muted"
                    }`}>
                      {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className="text-xs hidden md:block">{step.title}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {(() => {
                  const Icon = STEPS[currentStep - 1].icon;
                  return <Icon className="w-5 h-5" />;
                })()}
                {STEPS[currentStep - 1].title}
              </CardTitle>
              <CardDescription>
                Step {currentStep} of {STEPS.length}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderStep()}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              
              {currentStep < STEPS.length ? (
                <Button onClick={() => setCurrentStep(currentStep + 1)}>
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={submitReferral}
                  disabled={isSubmitting}
                  data-testid="submit-referral"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Submit Referral</>
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
      
      
    </div>
  );
}
